import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import styled from '~/app/components/styled'
import SubHeader, { SubHeaderFoldButton } from '~/app/components/common/SubHeader/SubHeader'

/**
 * Fold component
 * author: soojin.oh
 */

// type definitions
type FoldProps = {
  title: string
  initFolded?: boolean
  className?: string
  childHeightKey?: number
} & Pick<React.HTMLProps<HTMLDivElement>, 'style'>

// styled components
const Container = styled.div`
  position: relative;
  background: ${({ theme }) => theme.color.white};
  width: 100%;
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray200};
  box-shadow: 0 0.1rem 0.5rem 0 ${({ theme }) => theme.color.black_op5};
`

const ChildrenContainer = styled.div<{ height: number }>`
  opacity: 1;
  max-height: ${({ height }) => height}px;
  transition:
    max-height 0.4s ease,
    opacity 0.1s ease-in;

  &.folded {
    max-height: 0;
    opacity: 0;
  }
`

// Fold component
const Fold: React.FunctionComponent<PropsWithChildren<FoldProps>> = function ({
  title,
  initFolded = false,
  className = '',
  style,
  children,
  childHeightKey,
}) {
  const [isFolded, setFolded] = useState(initFolded)
  const childRef = useRef<HTMLDivElement>(null)
  const [childHeight, setChildHeight] = useState(0)
  useEffect(
    () => setChildHeight(childRef.current?.offsetHeight || 0),
    [childRef.current?.offsetHeight, children, childHeightKey]
  )
  useEffect(() => {
    const setHeight = () => setChildHeight(childRef.current?.offsetHeight || 0)
    window.addEventListener('resize', setHeight)
    return () => window.removeEventListener('resize', setHeight)
  }, [])
  return (
    <Container className={className} style={style}>
      <SubHeader
        title={title}
        right={
          children && (
            <SubHeaderFoldButton isOpen={!isFolded} onClick={() => setFolded(!isFolded)} />
          )
        }
        hasBottomContent={!isFolded}
        isAvailableTransition={true}
      />
      <ChildrenContainer height={childHeight} className={isFolded ? 'folded' : ''}>
        <div ref={childRef}>{children}</div>
      </ChildrenContainer>
    </Container>
  )
}

export default Fold
